import { useMemo } from 'react'
import { useTypeaheadExtractionUrlUpdate } from './useTypeaheadExtractionUrlUpdate'

interface UseSearchBlockingStateParams {
  extracting: string | undefined
  typeaheadId: string | undefined
  query: string | undefined
  windowSearch: string
  pathname: string
}

interface UseSearchBlockingStateReturn {
  /** Whether search should be blocked (extraction in progress) */
  blockSearch: boolean
  /** Whether extraction is currently in progress */
  isExtracting: boolean
  /** Whether extraction has completed */
  isExtractionComplete: boolean
}

/**
 * Hook to manage search blocking state during typeahead extraction.
 * Consolidates the shared logic between HotelSearchPage and HotelSearchMapPage.
 *
 * When a natural language search is submitted, the search is "blocked" while
 * the typeahead streaming extracts search parameters. During this time:
 * - The offer list should not fetch new results
 * - The map should not update its bounds
 * - Loading indicators should be shown
 *
 * Once extraction completes, the URL is updated with extracted params and
 * the search is unblocked.
 */
export function useSearchBlockingState({
  extracting,
  typeaheadId,
  query,
  windowSearch,
  pathname,
}: UseSearchBlockingStateParams): UseSearchBlockingStateReturn {
  const { isSearchBlocked, isExtracting, isExtractionComplete } = useTypeaheadExtractionUrlUpdate({
    extracting,
    typeaheadId,
    query: query,
    windowSearch,
    pathname,
  })

  // isSearchBlocked is the primary check from the extraction hook.
  // The extracting === 'true' fallback handles the edge case where
  // extraction just completed but the URL hasn't been updated yet.
  const blockSearch = useMemo(() => {
    return isSearchBlocked || extracting === 'true'
  }, [isSearchBlocked, extracting])

  return {
    blockSearch,
    isExtracting,
    isExtractionComplete,
  }
}
